import { MenuTreeItem } from '@shape-ds/components';

const descendantsCache = new Map<string, Set<string>>();

const getAllDescendantIds = (item: MenuTreeItem): Set<string> => {
  const cached = descendantsCache.get(item.id);
  if (cached) {
    return cached;
  }

  const descendants = new Set<string>();

  const collectDescendants = (node: MenuTreeItem) => {
    if (node.children) {
      node.children.forEach((child) => {
        descendants.add(child.id);
        collectDescendants(child);
      });
    }
  };

  collectDescendants(item);
  descendantsCache.set(item.id, descendants);
  return descendants;
};

export const getSelectionState = (
  asset: MenuTreeItem,
  selectedAssetsSet: Set<string>,
): { isSelected: boolean; isPartialSelected: boolean } => {
  const isSelected = selectedAssetsSet.has(asset.id);

  const hasChildren = asset.children && asset.children.length > 0;

  if (!hasChildren) {
    return { isSelected, isPartialSelected: false };
  }

  if (isSelected) {
    return { isSelected: true, isPartialSelected: false };
  }

  const descendantIds = getAllDescendantIds(asset);
  const hasSelectedDescendants = Array.from(descendantIds).some((id) => selectedAssetsSet.has(id));

  return { isSelected: false, isPartialSelected: hasSelectedDescendants };
};

export const createSelectedAssetsSet = (selectedAssets: string[]): Set<string> => {
  return new Set(selectedAssets);
};

export const clearDescendantsCache = () => {
  descendantsCache.clear();
};

export const getDirectChildrenIds = (item: MenuTreeItem): string[] => {
  return item.children ? item.children.map((child) => child.id) : [];
};

export const getHierarchicalSelection = (
  item: MenuTreeItem,
  currentSelectedAssets: string[],
): string[] => {
  const selectedSet = new Set(currentSelectedAssets);
  const isCurrentlySelected = selectedSet.has(item.id);
  const hasChildren = item.children && item.children.length > 0;

  if (!hasChildren) {
    return isCurrentlySelected
      ? currentSelectedAssets.filter((id) => id !== item.id)
      : [...currentSelectedAssets, item.id];
  }

  const descendantIds = getAllDescendantIds(item);
  const allDescendantsSelected = Array.from(descendantIds).every((id) => selectedSet.has(id));
  const someDescendantsSelected = Array.from(descendantIds).some((id) => selectedSet.has(id));

  if (!isCurrentlySelected && !someDescendantsSelected) {
    // Estado 1: Nada selecionado -> Seleciona pai + todos os filhos
    return [...currentSelectedAssets, item.id, ...Array.from(descendantIds)];
  } else if (isCurrentlySelected && allDescendantsSelected) {
    // Estado 2: Pai + todos filhos selecionados -> Desseleciona filhos, mantém pai
    return currentSelectedAssets.filter((id) => !descendantIds.has(id));
  } else {
    // Estado 3: Pai selecionado ou seleção parcial -> Desseleciona tudo
    return currentSelectedAssets.filter((id) => id !== item.id && !descendantIds.has(id));
  }
};
